import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatApiError } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import CheckboxGroup from "@/components/CheckboxGroup";

const emptyFormFrom = (fields) => Object.fromEntries(fields.map((f) => [f.name, f.type === "skills_multiselect" ? [] : ""]));

// Generic admin-managed master-data table: list + create + delete, driven entirely
// by a field schema, so Clients/Faculty/Skills/Infra/Software all share one
// implementation instead of four near-identical hand-rolled pages.
export default function DirectoryTable({ title, description, endpoint, columns, fields, canWrite, canDelete = canWrite, editable }) {
  const [items, setItems] = useState([]);
  const [skills, setSkills] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyFormFrom(fields));
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(endpoint);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [endpoint]);

  useEffect(() => {
    if (fields.some((f) => f.type === "skills_multiselect")) {
      api.get("/skills").then(({ data }) => setSkills(data)).catch(() => setSkills([]));
    }
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyFormFrom(fields));
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm(Object.fromEntries(fields.map((f) => [f.name, item[f.name] ?? (f.type === "skills_multiselect" ? [] : "")])));
    setOpen(true);
  };

  const submit = async () => {
    setLoading(true);
    try {
      if (editingId) {
        await api.put(`${endpoint}/${editingId}`, form);
        toast.success(`${title} entry updated`);
      } else {
        await api.post(endpoint, form);
        toast.success(`${title} entry created`);
      }
      setOpen(false);
      setEditingId(null);
      setForm(emptyFormFrom(fields));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`${endpoint}/${id}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const requiredOk = fields.filter((f) => f.required).every((f) => (Array.isArray(form[f.name]) ? form[f.name].length > 0 : form[f.name]));

  return (
    <div className="p-6 sm:p-10 max-w-[1200px] mx-auto page-enter">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Administration · Directory</div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight mt-1">{title}</h1>
          {description && <p className="text-sm text-muted-foreground mt-1 max-w-xl">{description}</p>}
        </div>
        {canWrite && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditingId(null); }}>
            <Button data-testid="dir-add" onClick={openCreate}><Plus className="w-4 h-4 mr-2" />Add</Button>
            <DialogContent>
              <DialogHeader><DialogTitle>{editingId ? "Edit" : "Add"} {title.replace(/s$/, "")}</DialogTitle></DialogHeader>
              <div className="grid gap-4">
                {fields.map((f) => (
                  <div className="space-y-2" key={f.name}>
                    <Label>{f.label}</Label>
                    {f.type === "textarea" ? (
                      <Textarea data-testid={`dir-${f.name}`} rows={3} value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} />
                    ) : f.type === "select" ? (
                      <Select value={form[f.name] || ""} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
                        <SelectTrigger data-testid={`dir-${f.name}`}><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent>
                          {(f.options || []).map((opt) => {
                            const { value, label } = typeof opt === "object" ? opt : { value: opt, label: opt };
                            return <SelectItem key={value} value={value}>{label}</SelectItem>;
                          })}
                        </SelectContent>
                      </Select>
                    ) : f.type === "skills_multiselect" ? (
                      <CheckboxGroup
                        testId={`dir-${f.name}`}
                        options={skills.map((s) => ({ value: s.id, label: s.name }))}
                        value={form[f.name] || []}
                        onToggle={(id) => {
                          const cur = form[f.name] || [];
                          setForm({ ...form, [f.name]: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] });
                        }}
                        onSelectAll={() => {
                          const all = skills.map((s) => s.id);
                          const cur = form[f.name] || [];
                          setForm({ ...form, [f.name]: cur.length === all.length ? [] : all });
                        }}
                      />
                    ) : (
                      <Input data-testid={`dir-${f.name}`} type={f.type || "text"} value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} />
                    )}
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button data-testid="dir-create-submit" onClick={submit} disabled={loading || !requiredOk}>{editingId ? "Save" : "Create"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="mt-8 border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((c) => <TableHead key={c.key}>{c.label}</TableHead>)}
                {(canWrite || canDelete) && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.id} data-testid={`dir-row-${it.id}`}>
                  {columns.map((c) => (
                    <TableCell key={c.key} className={c.key === columns[0].key ? "font-medium" : "text-sm"}>
                      {c.render ? c.render(it, skills) : (it[c.key] ?? "-")}
                    </TableCell>
                  ))}
                  {(canWrite || canDelete) && (
                    <TableCell className="text-right">
                      {editable && canWrite && (
                        <Button variant="ghost" size="sm" data-testid={`dir-edit-${it.id}`} onClick={() => openEdit(it)}><Pencil className="w-4 h-4" /></Button>
                      )}
                      {canDelete && (
                        <Button variant="ghost" size="sm" data-testid={`dir-delete-${it.id}`} onClick={() => del(it.id)}><Trash2 className="w-4 h-4" /></Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow><TableCell colSpan={columns.length + 1} className="text-center text-sm text-muted-foreground py-8">No entries yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
